import { pageTitle } from '@palaccount/config';
import type { BankOverviewResponse } from '@palaccount/contracts';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Bell, Info, Scale } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { useBankOverview } from './api';
import { BankingTabs } from './banking-tabs';

/**
 * FIN-12 — Bank Operations: the read-only morning page. Creates, changes and
 * posts nothing (BR-FIN-12-01). The date range governs ONLY the cash-flow
 * summary and the activity feed — and the interface says so (BR-FIN-12-02,
 * Q-FIN-12-1). The account tile is the product's one true deep link: it
 * opens Bank Accounts on that account with the Reconciliation tab showing
 * (BR-FIN-12-07). The bounced alert offers a route to BOTH cheque screens
 * (BR-FIN-12-06 — the prototype sent everyone to Incoming). The low-balance
 * threshold is the checks.low_balance_alert_threshold COMPANY setting, not a
 * session scribble.
 */
/**
 * BR-FIN-12-12 — where an activity reference lands. Every branch is a
 * navigation that SELECTS: the voucher and transfer screens are per-record
 * routes, the cheque registers seed their selection from `?check=`, and the
 * journal fallback opens the entry itself.
 */
function sourcePath(
  source: BankOverviewResponse['recentActivity'][number]['source'],
): string {
  switch (source.kind) {
    case 'receipt':
      return `/accounting/receipts/${source.id}`;
    case 'payment':
      return `/accounting/payments/${source.id}`;
    case 'transfer':
      return `/banks/transfers/${source.id}`;
    case 'check':
      return `/banks/checks/${source.direction ?? 'incoming'}?check=${source.id}`;
    case 'journal':
      return `/accounting/journals/${source.id}`;
  }
}

export function BankOperationsPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data, isPending, isError } = useBankOverview({
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  });

  useEffect(() => {
    document.title = pageTitle(t('banking.opsTitle'));
  }, [t]);

  if (isPending) return <div className="h-60 animate-pulse rounded-md bg-muted" aria-busy />;
  if (isError || !data) {
    return (
      <p role="alert" className="py-8 text-center text-sm text-destructive">
        {t('common.loadFailed')}
      </p>
    );
  }

  const hasAlerts =
    data.alerts.lowBalance.length > 0 ||
    data.alerts.unreconciledCount > 0 ||
    data.alerts.bouncedCount > 0;

  const incomingTiles = data.chequeTiles.filter((tile) => tile.direction === 'incoming');
  const outgoingTiles = data.chequeTiles.filter((tile) => tile.direction === 'outgoing');

  return (
    <div className="grid gap-4">
      <BankingTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('banking.opsTitle')}</h2>
        {/* BR-FIN-12-02: the range's remit, stated out loud. */}
        <span className="text-xs text-muted-foreground">{t('banking.opsRangeHint')}</span>
        <Label htmlFor="ops-from" className="sr-only">
          {t('banking.opsFrom')}
        </Label>
        <Input
          id="ops-from"
          type="date"
          className="h-9 w-auto"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
        />
        <Label htmlFor="ops-to" className="sr-only">
          {t('banking.opsTo')}
        </Label>
        <Input
          id="ops-to"
          type="date"
          className="h-9 w-auto"
          value={to}
          onChange={(event) => setTo(event.target.value)}
        />
      </div>

      {/* The alerts strip — ABSENT when nothing is wrong (BR-FIN-12-04).

          ERR-FIN-12-05 is the else-branch. The §14 row's advice was "nothing —
          absence is the message", which is the prototype making a virtue of a
          gap: the defects table records that "All clear — no alerts." existed
          in the dictionary and was never shown. Both halves hold now, and
          they do not conflict — the STRIP is absent (BR-FIN-12-04) and a
          sentence takes its place, so a reader can tell "nothing is wrong"
          from "the page did not load". */}
      {hasAlerts ? (
        <div className="flex flex-wrap gap-3" data-testid="alerts-strip">
          {data.alerts.lowBalance.map((alert) => (
            <div
              key={alert.bankAccountId}
              className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm dark:border-red-900 dark:bg-red-950"
            >
              <AlertTriangle aria-hidden className="size-4 text-red-600 dark:text-red-400" />
              <span>
                {t('banking.alertLowBalance', { name: alert.name })}{' '}
                <Amount value={alert.balance} currency={alert.currency} />
                {/*
                 * FIN-12 §16 — "compared and displayed in the same currency".
                 * The threshold at the foot of this page is in base, and the
                 * comparison that raised this alert was made in base, so the
                 * base figure is the one that explains the verdict. Without
                 * it the card read "€4,180.00" under a "$10,000.00" threshold
                 * — a single plausible number with nothing beside it to
                 * contradict it, which is the shape this build keeps finding.
                 *
                 * ONLY when the currencies differ: a domestic account would
                 * otherwise print its own figure twice ("$3,000.00 ≈
                 * $3,000.00"), which reads as two different amounts. GEN-116
                 * and ADM-01 both land on that — a single-currency business
                 * "should never see the word currency".
                 */}
                {alert.currency === data.baseCurrency ? null : (
                  /*
                   * `dir="ltr"` on the WRAPPER, not just on the figure inside
                   * it: "≈" is a bidi-neutral character, and a neutral left
                   * loose in an RTL paragraph beside an LTR isolate is placed
                   * by the PARAGRAPH's direction — so in Arabic it lands on
                   * the far side of the number it qualifies. `<Amount>` is
                   * already an island; the approximation sign has to be
                   * inside the same one. This is amount.tsx's own rule for a
                   * run of figures sharing an island, applied to a neutral
                   * that introduces one.
                   */
                  <span dir="ltr" className="ms-1 text-muted-foreground">
                    ≈ <Amount value={alert.baseBalance} currency={data.baseCurrency} />
                  </span>
                )}
              </span>
            </div>
          ))}
          {data.alerts.unreconciledCount > 0 ? (
            <Link
              to="/banks/accounts"
              className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950"
            >
              <Scale aria-hidden className="size-4 text-amber-700 dark:text-amber-400" />
              {t('banking.alertUnreconciled', { count: data.alerts.unreconciledCount })}
            </Link>
          ) : null}
          {data.alerts.bouncedCount > 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm dark:border-red-900 dark:bg-red-950">
              <AlertTriangle aria-hidden className="size-4 text-red-600 dark:text-red-400" />
              {t('banking.alertBounced', { count: data.alerts.bouncedCount })}
              {/* BR-FIN-12-06: a route to BOTH screens.

                  ERR-FIN-12-05's sibling, ERR-FIN-12-02, is answered here: the
                  prototype counted both directions and then sent everyone to
                  Incoming, so half the chase ended in a screen that could not
                  hold the cheque. The count is NOT an anchor — it is plain
                  text beside two links — and the test asserts that absence,
                  because "an Outgoing link exists somewhere" would still pass
                  on a page that wrapped the whole alert in the old one. */}
              <Link className="underline" to="/banks/checks/incoming">
                {t('banking.tabIncoming')}
              </Link>
              <Link className="underline" to="/banks/checks/outgoing">
                {t('banking.tabOutgoing')}
              </Link>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('banking.allClear')}</p>
      )}

      {/**
       * GEN-115 "directly above the numbers", GEN-116, GEN-117 — the same
       * band the financial and sales dashboards draw, phrased from the same
       * dictionary entry rather than a sentence invented for this screen.
       *
       * The SERVER decides whether a conversion happened: `rateDate` is
       * non-null exactly when one did, so the condition below IS the rule and
       * not the browser's guess at it. A single-currency company sees nothing
       * here — no line, no second figure on any tile, no mention of currency
       * anywhere (ADM-01).
       *
       * GEN-117: informational grey with the info icon, never amber and never
       * red — a statement of fact, not a warning. The ERR-DSH-04-04 notes ride
       * the same band because they are the other half of the same fact: what
       * could NOT be converted, and is therefore missing from the combined
       * total below. F-27's rule is that a figure is either converted and
       * disclosed or excluded and named, and this band is where both are said.
       */}
      {!data.rateDate && data.conversionNotes.length === 0 ? null : (
        <div className="flex flex-col gap-1">
          {!data.rateDate ? null : (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info aria-hidden className="size-3.5 shrink-0" />
              {t('dash.consolidated', { currency: data.baseCurrency, date: data.rateDate })}
            </p>
          )}
          {data.conversionNotes.map((note) => (
            <p key={note} className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info aria-hidden className="size-3.5 shrink-0" />
              {note}
            </p>
          ))}
        </div>
      )}

      {/* Accounts overview + the combined total. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <span className="me-auto">{t('banking.accountsOverview')}</span>
            <span className="text-sm font-normal text-muted-foreground">
              {t('banking.combinedTotal')}
            </span>
            {/* BR-FIN-09-14 — §5b puts this same figure in four places, and
                the red must travel with it (full note at FIN-09's account
                list). */}
            <Amount
              value={data.combinedTotal}
              currency={data.baseCurrency}
              className={
                data.combinedTotal.startsWith('-')
                  ? 'text-red-700 dark:text-red-400'
                  : undefined
              }
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {data.accounts.map((account) => (
            <Link
              key={account.bankAccountId}
              // BR-FIN-12-07: the deep link — that account, Reconciliation open.
              to={`/banks/accounts?account=${account.bankAccountId}&tab=reconciliation`}
              data-testid={`account-tile-${account.name}`}
              className="rounded-lg border p-3 transition-shadow hover:shadow-md"
            >
              <span className="flex items-center gap-2 text-sm font-semibold">
                <span className="me-auto">{account.name}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {account.currency}
                </span>
              </span>
              <span className="mt-1 block text-lg font-bold">
                {account.balance === null ? (
                  <span className="text-sm font-normal text-amber-700 dark:text-amber-400">
                    {t('banking.unmappedWarn')}
                  </span>
                ) : (
                  /* BR-FIN-09-14 on the tile (full note at FIN-09's account
                     list). The ≈ base aside below stays muted on purpose: it
                     restates THIS figure, which already carries the red, and
                     a red disclosure line would make the aside shout. */
                  <Amount
                    value={account.balance}
                    currency={account.currency}
                    className={
                      account.balance.startsWith('-')
                        ? 'text-red-700 dark:text-red-400'
                        : undefined
                    }
                  />
                )}
              </span>
              {/*
               * The same fact as the alert above, one size larger. These tiles
               * printed NATIVE balances directly beneath a combined total
               * stated in base — three currencies stacked in a row, each
               * correct on its own and none of them addable to the number in
               * the header. `baseBalance` was already on the wire and drawn
               * nowhere, so nothing new is computed and nothing is converted
               * in the browser; the figure that fed the total is simply shown
               * beneath the figure the account actually holds.
               *
               * Null `baseBalance` is a currency with no rate: it is NOT in
               * the combined total either, and `conversionNotes` names it
               * above rather than a bare "≈" implying a conversion happened.
               */}
              {account.balance !== null &&
              account.baseBalance !== null &&
              account.currency !== data.baseCurrency ? (
                <span
                  dir="ltr"
                  className="mt-0.5 block text-xs font-normal text-muted-foreground"
                >
                  ≈ <Amount value={account.baseBalance} currency={data.baseCurrency} />
                </span>
              ) : null}
              <span
                className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  account.reconciliationState === 'reconciled'
                    ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                }`}
              >
                {account.reconciliationState === 'reconciled'
                  ? t('banking.chipReconciled')
                  : t('banking.chipPending')}
              </span>
            </Link>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Cash flow — governed by the range. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('banking.cashFlow')}{' '}
              <span className="text-xs font-normal text-muted-foreground">
                {t('banking.opsRangeHint')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">{t('banking.cashIn')}</p>
              <p className="font-bold text-green-700 dark:text-green-400">
                <Amount value={data.cashFlow.totalIn} currency={data.baseCurrency} />
              </p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">{t('banking.cashOut')}</p>
              <p className="font-bold text-red-700 dark:text-red-400">
                <Amount value={data.cashFlow.totalOut} currency={data.baseCurrency} />
              </p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">{t('banking.cashNet')}</p>
              <p
                className={`font-bold ${
                  Number(data.cashFlow.net) < 0
                    ? 'text-red-700 dark:text-red-400'
                    : 'text-green-700 dark:text-green-400'
                }`}
              >
                <Amount value={data.cashFlow.net} currency={data.baseCurrency} />
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cheques dashboard — voided/cancelled/returned excluded (BR-FIN-12-10). */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('banking.chequesDashboard')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            {(
              [
                ['incoming', incomingTiles],
                ['outgoing', outgoingTiles],
              ] as const
            ).map(([band, tiles]) => (
              <div key={band}>
                <p className="mb-1 text-xs font-medium text-muted-foreground">
                  {band === 'incoming' ? t('banking.bandIncoming') : t('banking.bandOutgoing')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {tiles.length === 0 ? (
                    <span className="text-xs text-muted-foreground">{t('banking.noCheques')}</span>
                  ) : (
                    tiles.map((tile) => (
                      <span
                        key={`${tile.direction}-${tile.status}`}
                        className="rounded-md border px-2 py-1 text-xs"
                      >
                        {t(`banking.status.${tile.status}`)} · {tile.count}
                      </span>
                    ))
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Upcoming due cheques — the checks.reminder_days window drives it. */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Bell aria-hidden className="size-4" />
              <span className="me-auto">{t('banking.upcoming')}</span>
              {/* The window, and nothing about delivery. Delegated ruling D15
                  (26 Aug 2026) made every notification in-app and retired
                  `checks.reminder_method`; a line reading "Email" off a row
                  nothing honoured was the reason this one changed. */}
              <span className="text-xs font-normal text-muted-foreground">
                {t('banking.reminderWindow', { count: data.reminderDays })}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('banking.upcomingEmpty')}</p>
            ) : (
              <ul className="grid gap-2">
                {data.upcoming.map((row) => (
                  /* BR-FIN-12-12 / ERR-FIN-12-03 — the row is a LINK, and the
                     navigation selects the cheque it names: `?check=<id>` is
                     the register's selection-seeding landing point (built for
                     BR-ADM-04-06, shared here). Each direction goes to its
                     OWN screen — an outgoing cheque must not land on Incoming
                     (ERR-FIN-12-02's lesson, applied to the row). */
                  <li key={row.checkId} className="text-sm">
                    <Link
                      to={`/banks/checks/${row.direction}?check=${row.checkId}`}
                      className="flex items-center gap-2 rounded-md p-1 -m-1 hover:bg-muted"
                    >
                      {row.direction === 'incoming' ? (
                        <ArrowDownLeft aria-hidden className="size-4 text-green-700 dark:text-green-400" />
                      ) : (
                        <ArrowUpRight aria-hidden className="size-4 text-red-600 dark:text-red-400" />
                      )}
                      <span className="me-auto">
                        <span className="font-medium">{row.party}</span>{' '}
                        <Ltr value={row.code} className="font-mono text-xs text-muted-foreground" />
                      </span>
                      <Amount value={row.amount} currency={row.currency} />
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          row.dueInDays < 0
                            ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
                            : row.dueInDays <= data.reminderDays
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                              : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {row.dueInDays < 0
                          ? t('banking.overdueDays', { count: -row.dueInDays })
                          : t('banking.dueInDays', { count: row.dueInDays })}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Recent activity — governed by the range. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t('banking.recentActivity')}{' '}
              <span className="text-xs font-normal text-muted-foreground">
                {t('banking.opsRangeHint')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.recentActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('banking.activityEmpty')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.date')}</TableHead>
                    <TableHead>{t('banking.colAccount')}</TableHead>
                    <TableHead className="text-end">{t('banking.colAmount')}</TableHead>
                    <TableHead className="text-end">{t('banking.colReference')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recentActivity.map((row, index) => (
                    <TableRow key={`${row.entryId}-${index}`}>
                      <TableCell>
                        <Ltr value={row.date} className="font-mono text-xs" />
                      </TableCell>
                      <TableCell className="text-sm">{row.accountName}</TableCell>
                      <TableCell
                        className={`text-end ${
                          Number(row.amount) < 0
                            ? 'text-red-700 dark:text-red-400'
                            : 'text-green-700 dark:text-green-400'
                        }`}
                      >
                        <Amount value={row.amount} currency={row.currency} />
                      </TableCell>
                      {/* BR-FIN-12-12 — §5g "each row's reference is a link to
                          the screen that produced it", and the link SELECTS
                          the record it names: vouchers and transfers by their
                          per-record routes, a cheque via the register's
                          `?check=` landing point, and a hand-posted movement
                          by its own journal entry — the server's `source`
                          decides, so no row is ever a dead end. */}
                      <TableCell className="text-end">
                        <Link to={sourcePath(row.source)} className="underline">
                          <Ltr value={row.source.code} className="font-mono text-xs" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* The company-wide alert threshold (the FIN-12 defect fixed): read
          here, changed on the settings screen through the §16.6 ladder. */}
      <p className="text-xs text-muted-foreground">
        {t('banking.thresholdNote')}{' '}
        <Amount value={data.lowBalanceThreshold} currency={data.baseCurrency} /> ·{' '}
        <Link to="/settings" className="underline">
          {t('shell.navSettings')}
        </Link>
      </p>
    </div>
  );
}
